import React, { useState, useEffect } from 'react';
import { DashboardReferenceWidgetConfig, Dashboard, InputWidgetInfo } from '../../../../../types';
import { dashboardService } from '../../../../../services';
import { getWidgetLabel, getWidgetIcon } from '../../../registry';

interface DashboardReferenceWidgetBuilderProps {
  config: Partial<DashboardReferenceWidgetConfig>;
  onChange: (config: DashboardReferenceWidgetConfig) => void;
}

const DashboardReferenceWidgetBuilder: React.FC<DashboardReferenceWidgetBuilderProps> = ({
  config,
  onChange,
}) => {
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [inputWidgets, setInputWidgets] = useState<InputWidgetInfo[]>([]);
  const [selectedDashboardName, setSelectedDashboardName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingWidgets, setLoadingWidgets] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load dashboards on mount
  useEffect(() => {
    const loadDashboards = async () => {
      try {
        setLoading(true);
        const response = await dashboardService.getDashboards(1, 100);
        setDashboards(response.data);
      } catch (err) {
        setError('Failed to load dashboards');
      } finally {
        setLoading(false);
      }
    };
    loadDashboards();
  }, []);

  // Load input widgets when dashboard is selected
  useEffect(() => {
    if (!config.sourceDashboardId) {
      setInputWidgets([]);
      setSelectedDashboardName('');
      return;
    }

    const loadInputWidgets = async () => {
      try {
        setLoadingWidgets(true);
        const result = await dashboardService.getInputWidgets(config.sourceDashboardId!);
        setInputWidgets(result.widgets);
        setSelectedDashboardName(result.dashboardName);
      } catch (err) {
        setError('Failed to load widgets from dashboard');
        setInputWidgets([]);
      } finally {
        setLoadingWidgets(false);
      }
    };
    loadInputWidgets();
  }, [config.sourceDashboardId]);

  const handleDashboardChange = (dashboardId: string) => {
    onChange({
      sourceDashboardId: dashboardId || null,
      sourceDataKey: null,
      sourceWidgetType: null,
      readOnly: config.readOnly ?? true,
      displayTitle: config.displayTitle,
    });
  };

  const handleWidgetSelect = (widget: InputWidgetInfo) => {
    onChange({
      sourceDashboardId: config.sourceDashboardId || null,
      sourceDataKey: widget.dataKey,
      sourceWidgetType: widget.widgetType,
      readOnly: config.readOnly ?? true,
      displayTitle: config.displayTitle || widget.title,
    });
  };

  const handleReadOnlyChange = (readOnly: boolean) => {
    onChange({
      sourceDashboardId: config.sourceDashboardId || null,
      sourceDataKey: config.sourceDataKey || null,
      sourceWidgetType: config.sourceWidgetType || null,
      readOnly,
      displayTitle: config.displayTitle,
    });
  };

  const handleDisplayTitleChange = (displayTitle: string) => {
    onChange({
      sourceDashboardId: config.sourceDashboardId || null,
      sourceDataKey: config.sourceDataKey || null,
      sourceWidgetType: config.sourceWidgetType || null,
      readOnly: config.readOnly ?? true,
      displayTitle: displayTitle || undefined,
    });
  };

  const selectedWidget = inputWidgets.find(w => w.dataKey === config.sourceDataKey);

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 text-sm text-primary-800">
        <strong>Dashboard Reference Widget</strong>
        <p className="mt-1">
          Reference data from Q&A, Conviction, or Form widgets on other dashboards.
          Changes can optionally sync back to the source.
        </p>
      </div>

      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-3 text-sm text-danger-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Step 1: Select Dashboard */}
      <div className="border rounded-lg p-4">
        <h4 className="font-medium text-neutral-900 mb-3 flex items-center gap-2">
          <span className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
          Select Source Dashboard
        </h4>

        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
            Loading dashboards...
          </div>
        ) : (
          <select
            value={config.sourceDashboardId || ''}
            onChange={(e) => handleDashboardChange(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="">-- Select a dashboard --</option>
            {dashboards.map((dashboard) => (
              <option key={dashboard.id} value={dashboard.id}>
                {dashboard.name}
              </option>
            ))}
          </select>
        )}

        {dashboards.length === 0 && !loading && (
          <p className="mt-2 text-sm text-neutral-500">
            No dashboards found. Create a dashboard with input widgets first.
          </p>
        )}
      </div>

      {/* Step 2: Select Widget */}
      {config.sourceDashboardId && (
        <div className="border rounded-lg p-4">
          <h4 className="font-medium text-neutral-900 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
            Select Widget to Reference
            {selectedDashboardName && (
              <span className="text-sm font-normal text-neutral-500">
                from "{selectedDashboardName}"
              </span>
            )}
          </h4>

          {loadingWidgets ? (
            <div className="flex items-center gap-2 text-neutral-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              Loading widgets...
            </div>
          ) : inputWidgets.length === 0 ? (
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-700">
              No input widgets found in this dashboard. Add Q&A, Conviction, or Form widgets first.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {inputWidgets.map((widget) => (
                <button
                  key={widget.widgetId}
                  onClick={() => handleWidgetSelect(widget)}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${
                    config.sourceDataKey === widget.dataKey
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getWidgetIcon(widget.widgetType)}</span>
                    <div className="flex-1">
                      <p className="font-medium text-neutral-900">{widget.title}</p>
                      <p className="text-sm text-neutral-500">
                        {getWidgetLabel(widget.widgetType)}
                        {widget.hasData && (
                          <span className="ml-2 text-success-600">• Has data</span>
                        )}
                      </p>
                    </div>
                    {config.sourceDataKey === widget.dataKey && (
                      <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Configure Options */}
      {config.sourceDataKey && (
        <div className="border rounded-lg p-4">
          <h4 className="font-medium text-neutral-900 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
            Configure Options
          </h4>

          <div className="space-y-4">
            {/* Display Title */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Display Title (optional)
              </label>
              <input
                type="text"
                value={config.displayTitle || ''}
                onChange={(e) => handleDisplayTitleChange(e.target.value)}
                placeholder={selectedWidget?.title || 'Override widget title'}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <p className="mt-1 text-xs text-neutral-500">
                Leave empty to use the source widget's title
              </p>
            </div>

            {/* Read-Only Toggle */}
            <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
              <div>
                <p className="font-medium text-neutral-900">Read-Only Mode</p>
                <p className="text-sm text-neutral-500">
                  {config.readOnly
                    ? 'Widget displays data but changes are not saved'
                    : 'Changes sync back to the source dashboard'
                  }
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleReadOnlyChange(!config.readOnly)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  config.readOnly ? 'bg-primary-600' : 'bg-neutral-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${
                    config.readOnly ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {!config.readOnly && (
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-700">
                <strong>Warning:</strong> With read-only disabled, any changes made to this widget
                will be saved directly to the source dashboard.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Configuration Summary */}
      {config.sourceDashboardId && config.sourceDataKey && (
        <div className="border rounded-lg p-4 bg-neutral-50">
          <h4 className="font-medium text-neutral-900 mb-2">Configuration Summary</h4>
          <div className="text-sm space-y-1">
            <p><span className="text-neutral-500">Source Dashboard:</span> {selectedDashboardName}</p>
            <p><span className="text-neutral-500">Widget Type:</span> {config.sourceWidgetType ? getWidgetLabel(config.sourceWidgetType) : '-'}</p>
            <p><span className="text-neutral-500">Data Key:</span> <code className="bg-neutral-200 px-1 rounded">{config.sourceDataKey}</code></p>
            <p><span className="text-neutral-500">Mode:</span> {config.readOnly ? 'Read-only' : 'Editable (syncs back)'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardReferenceWidgetBuilder;
